import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import {
  queryFilterOptions,
  type FilterDef,
  type FilterOption,
} from '@nucleus-commerce/admin-kit/index-table';
import type { ProductSummary, StoreProductListItem } from '@nucleus-commerce/api-client/admin';

import { productListQueryOptions, productQueryOptions } from '@/features/products';

/** Fields GET /stores/{storeId}/products sorts on; anything else is a 400. */
export const STORE_PRODUCT_SORTS = ['title', 'createdAt', 'publishedAt', 'price'] as const;

/**
 * The store catalog's filter on top of the shared product filters (`useProductFilterDefs` in
 * store scope, D-182): one master product, which the store list takes and a selection does not.
 * It narrows the list to one row at most, so "Select all N" never covers it.
 */
export const STORE_PRODUCT_EXTRA_FIELDS = [{ key: 'productId', type: 'async' }] as const;

const productOption = (product: Pick<ProductSummary, 'id' | 'title' | 'handle'>): FilterOption => ({
  value: product.id,
  label: product.title,
  description: product.handle,
});

export interface StoreProductFiltersOptions {
  /** The `storeCatalog` namespace. */
  t: TFunction;
  queryClient: QueryClient;
}

/** The store catalog's own filters, listed after the shared ones. */
export function storeProductExtraFilters({
  t,
  queryClient,
}: StoreProductFiltersOptions): Array<FilterDef<StoreProductListItem>> {
  return [
    {
      key: 'productId',
      label: t('filters.product'),
      type: 'async',
      // The endpoint takes one master product.
      multiple: false,
      aliases: ['product'],
      ...queryFilterOptions(queryClient, {
        search: (q) => productListQueryOptions({ ...(q ? { q } : {}), limit: 20 }),
        toOptions: (page) => page.items.map(productOption),
        resolve: {
          query: ([id]) => productQueryOptions(id ?? ''),
          toOptions: (product) => [productOption(product)],
        },
      }),
    },
  ];
}
